import { useEffect, useState } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { AppUpdateBanner } from '@/components/AppUpdateBanner';
import { useSidebarStore } from '@/stores/sidebar';
import { Sidebar } from './Sidebar';
import { Navbar } from './Navbar';
import { Breadcrumbs } from './Breadcrumbs';

// AppShell — layout principal autenticado.
//
// Decisión sobre mobile sidebar:
// - En mobile (<768px) NO renderizamos el sidebar fijo. En su lugar usamos un
//   drawer off-canvas controlado con framer-motion (ya en deps). Esto evita
//   forzar la dependencia de @radix-ui/react-dialog solo para esto.
// - El drawer se abre con un botón hamburguesa en el navbar y se cierra al
//   navegar o al hacer click sobre el backdrop.
//
// Decisión sobre la barra (como Effetá Apps):
// - La barra superior (`Navbar`, `h-16`) va a lo ancho de la pantalla y fija
//   arriba. El menú lateral de escritorio empieza debajo de ella (`top-16`).
//
// Decisión sobre breadcrumbs:
// - Los breadcrumbs se derivan del `useMatches()` del router. Cada route puede
//   declarar `handle: { breadcrumb: 'Productos' }`.
// - Van arriba del contenido, no en la barra. Fila de alto fijo (`h-5` más
//   `mb-4`, 2.25rem) que el Estudio de fotos descuenta en su alto. En el
//   celular no se muestran: cada página ya lleva su título grande.

export const AppShell = () => {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  // El embed de integradores necesita todo el ancho y alto disponible
  // (iframe full-viewport). En esa ruta saltamos padding + max-width del
  // main y dejamos que la page controle su geometria.
  const isIntegratorRoute = Boolean(useMatch('/integrators/:slug'));

  // Cerrar drawer al pasar a desktop (resize >= 768px) para evitar quedar
  // en estado inconsistente.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent): void => {
      if (e.matches) setMobileOpen(false);
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    return undefined;
  }, []);

  // Bloquear scroll del body cuando el drawer está abierto.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const original = document.body.style.overflow;
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = original;
    };
  }, [mobileOpen]);

  return (
    <div className="bg-bg text-text flex min-h-screen flex-col">
      <Navbar onOpenMobileSidebar={() => setMobileOpen(true)} />

      {/* Sidebar desktop fijo, debajo de la barra */}
      <aside
        className={cn(
          'fixed top-16 bottom-0 left-0 z-20 hidden md:flex',
          'transition-[width] duration-200 ease-out',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <Sidebar className="w-full" variant="desktop" />
      </aside>

      {/* Sidebar mobile (drawer) */}
      <AnimatePresence>
        {mobileOpen ? (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setMobileOpen(false)}
              className="bg-overlay/50 fixed inset-0 z-40 md:hidden"
              aria-hidden="true"
            />
            <motion.div
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
              className="fixed inset-y-0 left-0 z-50 w-72 max-w-[80vw] md:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Menú de navegación"
            >
              <Sidebar
                className="h-full"
                variant="mobile"
                onNavigate={() => setMobileOpen(false)}
              />
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>

      {/* Columna principal, debajo de la barra */}
      <div
        className={cn(
          'flex flex-1 flex-col transition-[padding] duration-200 ease-out',
          // Reservamos espacio para el sidebar fijo en desktop.
          collapsed ? 'md:pl-16' : 'md:pl-60',
        )}
      >
        {isIntegratorRoute ? (
          <main className="bg-bg flex-1">
            {/* El embed ocupa el resto de la pantalla: las migas van en una
                franja de alto fijo (`h-9`) que `IntegratorPage` descuenta. */}
            <div className="border-border hidden h-9 items-center border-b px-4 sm:flex md:px-6">
              <Breadcrumbs />
            </div>
            <Outlet />
          </main>
        ) : (
          <main className="bg-bg flex-1 p-4 md:p-6">
            <div className="mb-4 hidden h-5 items-center sm:flex">
              <Breadcrumbs />
            </div>
            {/* Sin tope de ancho: el admin usa el monitor completo. Quien
                necesite una medida legible la pide en su `PageContainer`
                (narrow/medium/wide siguen acotando forms y detalles). */}
            <div className="w-full">
              <Outlet />
            </div>
          </main>
        )}
      </div>

      {/* Aviso de versión nueva. Montado una sola vez acá para que aparezca en
          toda página autenticada. Es `fixed`: no altera el layout del main. */}
      <AppUpdateBanner />
    </div>
  );
};
